import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAiAgent } from '@/contexts/AiAgentContext';
import type { AgentMessage } from '@/components/ai-agent/AgentMessages';
import type { ActionPayload } from '@/components/ai-agent/action-payload';
import {
  createConversation,
  fetchConversation,
  fetchConversations,
  patchConversation,
  searchConversations,
  streamAiAgentChat,
  type AiConversation,
  type AiConversationMessage,
  type AiSseChunk,
  type ConversationMode,
} from '@/services/aiAgent.service';

function genId(): string {
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getOrCreateSessionId(): string {
  try {
    const k = 'ai_workspace_session_id';
    const existing = sessionStorage.getItem(k);
    if (existing) return existing;
    const sid = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `sid-${Date.now()}`;
    sessionStorage.setItem(k, sid);
    return sid;
  } catch {
    return `sid-${Date.now()}`;
  }
}

function messagesFromStored(stored: AiConversationMessage[]): AgentMessage[] {
  const out: AgentMessage[] = [];
  for (const m of stored) {
    if (m.role === 'user') {
      out.push({ id: m.id, role: 'user', text: m.content });
      continue;
    }
    if (m.role === 'assistant') {
      out.push({ id: m.id, role: 'assistant', text: m.content });
      const toolResults = Array.isArray(m.metadata?.toolResults) ? m.metadata!.toolResults : [];
      toolResults.forEach((data, idx) => {
        out.push({ id: `${m.id}-tool-${idx}`, role: 'tool', toolName: 'queryERPData', data });
      });
      continue;
    }
    if (m.role === 'tool') {
      out.push({ id: m.id, role: 'tool', toolName: 'tool', data: m.metadata ?? m.content });
    }
  }
  return out;
}

export function useAiWorkspace() {
  const { pageContext } = useAiAgent();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState<AiConversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<AiConversation | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mode, setMode] = useState<ConversationMode>('assistant');
  const [contextFilters, setContextFilters] = useState<Record<string, unknown>>({});
  const sessionIdRef = useRef(getOrCreateSessionId());
  const lastUserMessageRef = useRef('');
  const lastToolNameRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const messageCacheRef = useRef<Map<string, AgentMessage[]>>(new Map());
  const loadingConversationRef = useRef<string | null>(null);
  const conversationsRef = useRef<AiConversation[]>([]);
  const [conversationLoading, setConversationLoading] = useState(false);

  conversationsRef.current = conversations;

  const refreshList = useCallback(async (silent = false) => {
    if (!silent) setListLoading(true);
    try {
      const rows = searchQuery.trim()
        ? await searchConversations(searchQuery.trim())
        : await fetchConversations({ archived: false });
      setConversations(rows);
    } finally {
      if (!silent) setListLoading(false);
    }
  }, [searchQuery]);

  const loadConversation = useCallback(async (id: string) => {
    if (loadingConversationRef.current === id) return;
    loadingConversationRef.current = id;
    setConversationLoading(true);

    const cached = messageCacheRef.current.get(id);
    const fromList = conversationsRef.current.find((c) => c.id === id);
    if (fromList) setActiveConversation(fromList);
    if (cached) {
      setMessages(cached);
    } else {
      setMessages([]);
    }

    try {
      const { conversation, messages: stored } = await fetchConversation(id);
      if (loadingConversationRef.current !== id) return;
      const nextMessages = messagesFromStored(stored);
      messageCacheRef.current.set(id, nextMessages);
      setActiveConversation(conversation);
      setMode(conversation.mode || 'assistant');
      setMessages(nextMessages);
      setSearchParams({ conversationId: id }, { replace: true });
    } finally {
      if (loadingConversationRef.current === id) {
        loadingConversationRef.current = null;
        setConversationLoading(false);
      }
    }
  }, [setSearchParams]);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  useEffect(() => {
    const id = searchParams.get('conversationId');
    if (id && id !== activeConversation?.id && loadingConversationRef.current !== id) {
      void loadConversation(id);
    }
  }, [searchParams, activeConversation?.id, loadConversation]);

  const applyChunk = useCallback((c: AiSseChunk, assistantId: string) => {
    if (c.type === 'meta' && c.conversationId) {
      setSearchParams({ conversationId: c.conversationId });
      return;
    }
    if (c.type === 'text') {
      setMessages((prev) =>
        prev.map((x) => (x.id === assistantId && x.role === 'assistant' ? { ...x, text: x.text + c.content } : x))
      );
      return;
    }
    if (c.type === 'tool_start') {
      lastToolNameRef.current = c.toolName;
      return;
    }
    if (c.type === 'tool_result') {
      setMessages((prev) => [
        ...prev,
        { id: genId(), role: 'tool', toolName: lastToolNameRef.current || 'tool', data: c.data },
      ]);
      lastToolNameRef.current = null;
      return;
    }
    if (c.type === 'error') {
      setMessages((prev) => [...prev, { id: genId(), role: 'error', text: c.message }]);
    }
  }, [setSearchParams]);

  const runStream = useCallback(
    async (userText: string, conversationId: string, confirmed: boolean) => {
      const assistantId = genId();
      setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', text: '' }]);
      lastToolNameRef.current = null;
      abortRef.current = new AbortController();
      try {
        await streamAiAgentChat(
          {
            message: userText,
            sessionId: sessionIdRef.current,
            conversationId,
            pageContext: {
              ...pageContext,
              mode,
              filters: contextFilters,
            } as Record<string, unknown>,
            confirmed,
            enableSemanticSearch: true,
          },
          (chunk) => applyChunk(chunk, assistantId),
          { signal: abortRef.current.signal }
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setMessages((prev) => [...prev, { id: genId(), role: 'error', text: msg }]);
      } finally {
        abortRef.current = null;
        setMessages((prev) => {
          messageCacheRef.current.set(conversationId, prev);
          return prev;
        });
        void refreshList(true);
      }
    },
    [applyChunk, contextFilters, mode, pageContext, refreshList]
  );

  const startNewChat = useCallback(async () => {
    const conv = await createConversation({ mode });
    messageCacheRef.current.set(conv.id, []);
    setActiveConversation(conv);
    setMessages([]);
    setSearchParams({ conversationId: conv.id }, { replace: true });
    await refreshList(true);
    return conv;
  }, [mode, refreshList, setSearchParams]);

  const sendMessage = useCallback(
    async (text: string, confirmed = false) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      let convId = activeConversation?.id || searchParams.get('conversationId') || '';
      if (!convId) {
        const conv = await startNewChat();
        convId = conv.id;
      }
      lastUserMessageRef.current = trimmed;
      setMessages((prev) => [...prev, { id: genId(), role: 'user', text: trimmed }]);
      setLoading(true);
      try {
        await runStream(trimmed, convId, confirmed);
      } finally {
        setLoading(false);
      }
    },
    [activeConversation?.id, runStream, searchParams, startNewChat]
  );

  const updateConversationMeta = useCallback(
    async (patch: Parameters<typeof patchConversation>[1]) => {
      if (!activeConversation?.id) return;
      const updated = await patchConversation(activeConversation.id, patch);
      setActiveConversation(updated);
      await refreshList();
    },
    [activeConversation?.id, refreshList]
  );

  return {
    conversations,
    activeConversation,
    messages,
    loading,
    conversationLoading,
    listLoading,
    searchQuery,
    setSearchQuery,
    mode,
    setMode,
    contextFilters,
    setContextFilters,
    refreshList,
    loadConversation,
    startNewChat,
    sendMessage,
    updateConversationMeta,
    lastUserMessageRef,
    pageContext,
  };
}

export type UseAiWorkspaceConfirm = {
  confirmOpen: boolean;
  setConfirmOpen: (v: boolean) => void;
  confirmPayload: ActionPayload | null;
  setConfirmPayload: (p: ActionPayload | null) => void;
};
